import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, Paper, alpha, useTheme,
  Chip, LinearProgress, Alert, Divider, CircularProgress,
} from '@mui/material';
import { Timer, WarningAmber, CheckCircle, InfoOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import feeInvoiceService from '../../services/feeInvoiceService';
import SearchableSelect from '../common/SearchableSelect';
import { buildSessionOptions } from '../../utils/sessionOptions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { classesApi } from '../../services/sessions/classesApi';

import { LateFeePreview } from '../../types/fees';
import { Session, SessionClass } from '../../types/sessions';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  onClose: () => void;
  onApplied: (result: { processed: number; totalCharged: number }) => void;
  defaultSessionId?: string;
  defaultClassId?: string;
}

/**
 * Apply Late Fees — dry-run preview → confirm → commit. Every eligible invoice
 * gets a `FeeAdjustment` of kind='late_fee' appended and its outstanding
 * bumped by the computed charge. Idempotent — a re-run skips invoices whose
 * `lateFeeGeneratedAt` is already stamped.
 */
const ApplyLateFeesDialog: React.FC<Props> = ({
  open, onClose, onApplied, defaultSessionId, defaultClassId,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [classes, setClasses] = useState<SessionClass[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  const [sessionId, setSessionId] = useState('');
  const [classId, setClassId] = useState('');

  const [preview, setPreview] = useState<LateFeePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState<{ processed: number; totalCharged: number } | null>(null);

  const currencyFmt = useCurrencyFormat();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingCatalogs(true);
    (async () => {
      try {
        const [ss, cls] = await Promise.all([
          sessionsApi.list({ includeArchived: false }),
          classesApi.list(),
        ]);
        if (cancelled) return;
        setSessions(ss);
        setClasses(cls);
      } catch {
        if (!cancelled) enqueueSnackbar('Failed to load pickers', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCatalogs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  useEffect(() => {
    if (!open) return;
    setSessionId(defaultSessionId || '');
    setClassId(defaultClassId || '');
    setPreview(null);
    setResult(null);
    setPreviewError(null);
  }, [open, defaultSessionId, defaultClassId]);

  const runPreview = useCallback(async () => {
    setLoadingPreview(true);
    setPreviewError(null);
    setPreview(null);
    try {
      const res = await feeInvoiceService.previewApplyLateFees({
        sessionId: sessionId || null,
        classId:   classId   || null,
      });
      setPreview(res);
    } catch (e: any) {
      setPreviewError(e?.message || 'Could not compute preview');
    } finally {
      setLoadingPreview(false);
    }
  }, [sessionId, classId]);

  useEffect(() => {
    if (open) runPreview();
  }, [open, runPreview]);

  const commit = async () => {
    if (!preview || preview.eligibleCount === 0) return;
    setCommitting(true);
    try {
      const res = await feeInvoiceService.applyLateFees({
        sessionId: sessionId || null,
        classId:   classId   || null,
      });
      setResult({ processed: res.processed, totalCharged: res.totalCharged });
      onApplied({ processed: res.processed, totalCharged: res.totalCharged });
      enqueueSnackbar(
        `Applied late fees to ${res.processed} challan${res.processed === 1 ? '' : 's'} · ${currencyFmt(res.totalCharged)}`,
        { variant: 'success' },
      );
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Failed to apply late fees', { variant: 'error' });
    } finally {
      setCommitting(false);
    }
  };

  const policyKindLabel = (k: string) => {
    if (k === 'flat') return 'flat';
    if (k === 'percent') return 'percent of outstanding';
    if (k === 'daily_flat') return 'flat per day';
    if (k === 'daily_percent') return 'percent per day';
    return k;
  };

  return (
    <Dialog open={open} onClose={committing ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Timer color="warning" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Apply late fees</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Scan overdue challans past the grace window and stamp a late-fee line on each. Safe to re-run — challans that already have a late fee are skipped.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {loadingCatalogs && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {/* Scope */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, display: 'block', mb: 1 }}>
            Narrow the scope
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SearchableSelect
              label="Session"
              value={sessionId}
              onChange={setSessionId}
              options={buildSessionOptions(sessions, theme)}
              placeholder="All sessions"
              fullWidth
              minWidth={0}
            />
            <SearchableSelect
              label="Class"
              value={classId}
              onChange={setClassId}
              options={classes.map(c => ({ value: c._id, label: c.name }))}
              placeholder="All classes"
              fullWidth
              minWidth={0}
            />
          </Stack>
        </Paper>

        {loadingPreview && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Scanning challans…
            </Typography>
          </Box>
        )}
        {previewError && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{previewError}</Alert>}

        {preview && !loadingPreview && (
          <>
            {!preview.enabled ? (
              <Alert severity="warning" variant="outlined" icon={<InfoOutlined fontSize="small" />}>
                Late fee policy is disabled. Enable it in <strong>Finance → Setup → Due dates &amp; late fees</strong> before applying.
              </Alert>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6, flex: 1 }}>
                    Preview
                  </Typography>
                  {preview.policySnapshot && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        `${preview.policySnapshot.amount}${preview.policySnapshot.kind.includes('percent') ? '%' : ''} · ${policyKindLabel(preview.policySnapshot.kind)}` +
                        (preview.policySnapshot.graceDays ? ` · ${preview.policySnapshot.graceDays}d grace` : '')
                      }
                      sx={{
                        height: 22, fontSize: '0.7rem', fontWeight: 600,
                        borderColor: alpha(theme.palette.warning.main, 0.4),
                        color: theme.palette.warning.dark,
                        bgcolor: alpha(theme.palette.warning.main, 0.06),
                      }}
                    />
                  )}
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">Eligible challans</Typography>
                    <Typography variant="h6" fontWeight={700}>
                      {preview.eligibleCount.toLocaleString()}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">Projected charge</Typography>
                    <Typography variant="h6" fontWeight={700} color="warning.dark">
                      {currencyFmt(preview.totalCharge)}
                    </Typography>
                  </Box>
                  {preview.skippedAlreadyCharged > 0 && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">Skipped (already charged)</Typography>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                          {preview.skippedAlreadyCharged.toLocaleString()}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Stack>

                {preview.eligibleCount === 0 && (
                  <Alert severity="info" variant="outlined" icon={<CheckCircle fontSize="small" />}>
                    Nothing to charge — every overdue challan in this scope is already stamped, or none are past grace.
                  </Alert>
                )}
              </Paper>
            )}
          </>
        )}

        {committing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Applying late fees…
            </Typography>
          </Box>
        )}

        {result && (
          <Alert severity="success" variant="outlined" sx={{ mt: 2 }}>
            Applied to <strong>{result.processed}</strong> challan{result.processed === 1 ? '' : 's'} · <strong>{currencyFmt(result.totalCharged)}</strong> total charged.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={committing}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && preview?.enabled && (
          <Button
            variant="contained"
            color="warning"
            disableElevation
            onClick={commit}
            disabled={committing || !preview || preview.eligibleCount === 0}
            startIcon={<WarningAmber />}
          >
            {committing
              ? 'Applying…'
              : preview
                ? `Apply to ${preview.eligibleCount} challan${preview.eligibleCount === 1 ? '' : 's'}`
                : 'Apply late fees'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApplyLateFeesDialog;
